import { commonMiscLabels } from "@/i18n/labels/commonLabels";
import { getLanguage, type Language } from "@/utils/languageUtils";

/** Labels for sanitized error messages (i18n) */
function getErrorLabels(lang?: Language) {
  const misc = commonMiscLabels(lang ?? getLanguage()) as Record<string, string>;
  return {
    unexpectedError: misc.unexpectedError ?? "Ett oväntat fel uppstod. Vänligen försök igen.",
    unexpectedErrorWithSupport:
      misc.unexpectedErrorWithSupport ??
      "Ett oväntat fel uppstod. Vänligen försök igen eller kontakta support om problemet kvarstår.",
  };
}

/**
 * Sanitizes error messages to hide technical details from users
 * Removes stack traces, file paths, and other technical information
 */
export function sanitizeErrorMessage(error: unknown, lang?: Language): string {
  const labels = getErrorLabels(lang);

  if (!error) {
    return labels.unexpectedError;
  }

  // If it's already a string, check if it contains technical details
  if (typeof error === "string") {
    return sanitizeErrorString(error, labels);
  }

  // If it's an Error object, extract the message
  if (error instanceof Error) {
    return sanitizeErrorString(error.message, labels);
  }

  // If it has a message property
  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === "string") {
      return sanitizeErrorString(message, labels);
    }
  }

  return labels.unexpectedError;
}

/**
 * Sanitizes an error string by removing technical details
 */
function sanitizeErrorString(
  errorMessage: string,
  labels: ReturnType<typeof getErrorLabels>
): string {
  if (!errorMessage) {
    return labels.unexpectedError;
  }

  // Remove stack traces (lines starting with "at " or containing file paths)
  const lines = errorMessage.split("\n");
  const sanitizedLines: string[] = [];

  for (const line of lines) {
    // Skip stack trace lines
    if (
      line.trim().startsWith("at ") ||
      line.includes("node_modules") ||
      line.includes("C:\\") ||
      line.includes("C:/") ||
      line.includes("/") && (line.includes(".ts:") || line.includes(".js:")) ||
      line.includes("PrismaClient") ||
      line.includes("prisma.") ||
      line.includes("Invalid `prisma") ||
      line.includes("invocation in")
    ) {
      continue;
    }

    // Skip lines that are just file paths or technical details
    if (
      line.includes("→") ||
      line.includes("~~~~~~~~") ||
      line.trim().match(/^\d+\s+\|/) // Line numbers in stack traces
    ) {
      continue;
    }

    // Keep the line if it's not purely technical
    sanitizedLines.push(line);
  }

  const sanitized = sanitizedLines.join(" ").trim();

  // If we removed everything, return a generic message
  if (!sanitized || sanitized.length < 10) {
    return labels.unexpectedErrorWithSupport;
  }

  // If the message is still very long, truncate it
  if (sanitized.length > 200) {
    return sanitized.substring(0, 200) + "...";
  }

  return sanitized;
}

